/* import __COLOCATED_TEMPLATE__ from './trusted-senders.hbs'; */
/* RESPONSIBLE TEAM: team-email */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type IntlService from 'embercom/services/intl';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { isEmpty } from '@ember/utils';
import { get, post } from 'embercom/lib/ajax';
import { deleteRequest } from 'embercom/lib/inbox/requests';
import { isValidEmail } from 'embercom/lib/email';

interface Args {}

const domainRegex = /^[a-z0-9.-]+\.[a-z]{2,}$/i;

interface TrustedEmailAddress {
  id: string;
  email_address: string;
  app_id: string;
}

interface TrustedEmailAddressesResponse {
  trusted_email_addresses: TrustedEmailAddress[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

export default class TrustedSenders extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;

  @tracked showSideDrawer = false;
  @tracked trustedEmailAddresses: TrustedEmailAddress[] = [];
  @tracked newEmailAddress = '';
  @tracked newDomain = '';
  @tracked addEmailFormExpanded = false;
  @tracked addDomainFormExpanded = false;
  @tracked isLoading = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.loadTrustedEmailAddressesTask).perform();
  }

  get hasTrustedEmailAddresses() {
    return this.trustedEmailAddresses.length > 0;
  }

  get isLoadingData() {
    return taskFor(this.loadTrustedEmailAddressesTask).isRunning;
  }

  get processedTrustedList() {
    return this.trustedEmailAddresses.map((item) => ({
      ...item,
      isDomain: item.email_address.startsWith('*@'),
      displayAddress: this.getDisplayAddress(item.email_address),
      type: item.email_address.startsWith('*@') ? 'domain' : 'email',
    }));
  }

  @restartableTask
  *loadTrustedEmailAddressesTask(): TaskGenerator<void> {
    this.isLoading = true;

    try {
      // Load all pages of trusted email addresses
      let currentPage = 1;
      let totalPages = 1;
      let allAddresses: TrustedEmailAddress[] = [];

      do {
        let response: TrustedEmailAddressesResponse = yield get(
          `/ember/trusted_email_addresses?app_id=${this.appService.app.id}&page=${currentPage}&per_page=50`,
        );

        allAddresses.push(...response.trusted_email_addresses);
        currentPage++;
        totalPages = response.total_pages;
      } while (currentPage <= totalPages);

      this.trustedEmailAddresses = allAddresses;
    } catch (error) {
      console.error('Failed to load trusted email addresses:', error);
      this.notificationsService.notifyError(
        this.intl.t('new-settings.channels.email.advanced.trusted-senders.load-failed'),
      );
    } finally {
      this.isLoading = false;
    }
  }

  @action
  openSideDrawer() {
    this.showSideDrawer = true;
  }

  @action
  closeSideDrawer() {
    this.showSideDrawer = false;
  }

  @action
  expandAddEmailForm() {
    this.addEmailFormExpanded = true;
    this.addDomainFormExpanded = false;
  }

  @action
  collapseAddEmailForm() {
    this.addEmailFormExpanded = false;
    this.newEmailAddress = '';
  }

  @action
  expandAddDomainForm() {
    this.addDomainFormExpanded = true;
    this.addEmailFormExpanded = false;
  }

  @action
  collapseAddDomainForm() {
    this.addDomainFormExpanded = false;
    this.newDomain = '';
  }

  duplicateEmailAddress(emailAddress: string): boolean {
    return this.trustedEmailAddresses.some((item) => item.email_address === emailAddress);
  }

  isValidEmailAddress(emailAddress: string) {
    return !isEmpty(emailAddress) && isValidEmail(emailAddress);
  }

  isValidDomain(domain: string) {
    return !isEmpty(domain) && domainRegex.test(domain.toLowerCase());
  }

  getDisplayAddress(emailAddress: string): string {
    return emailAddress.startsWith('*@') ? emailAddress.replace(/^\*@/, '') : emailAddress;
  }

  @dropTask
  *deleteTrustedEmailAddress(trustedEmailAddress: TrustedEmailAddress): TaskGenerator<void> {
    try {
      yield deleteRequest(
        `/ember/trusted_email_addresses/${trustedEmailAddress.id}?app_id=${this.appService.app.id}`,
      );

      this.trustedEmailAddresses = this.trustedEmailAddresses.filter(
        (item) => item.id !== trustedEmailAddress.id,
      );

      let displayAddress = this.getDisplayAddress(trustedEmailAddress.email_address);

      this.notificationsService.notifyConfirmation(
        this.intl.t('new-settings.channels.email.advanced.trusted-senders.deleted-address', {
          address: displayAddress,
        }),
      );
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.channels.email.advanced.trusted-senders.not-deleted'),
      );
    }
  }

  @dropTask
  *addNewEmail(emailAddress: string): TaskGenerator<void> {
    if (!this.isValidEmailAddress(emailAddress)) {
      this.notificationsService.notifyWarning(
        this.intl.t('new-settings.channels.email.advanced.trusted-senders.enter-valid-email'),
      );
      return;
    }

    if (this.duplicateEmailAddress(emailAddress)) {
      this.notificationsService.notifyWarning(
        this.intl.t('new-settings.channels.email.advanced.trusted-senders.email-already-added'),
      );
      return;
    }

    this.collapseAddEmailForm();
    try {
      let response = yield post('/ember/trusted_email_addresses', {
        app_id: this.appService.app.id,
        email_address: emailAddress,
      });

      // Add to local list (no reload needed - smoother UX)
      this.trustedEmailAddresses = [...this.trustedEmailAddresses, response];

      this.notificationsService.notifyConfirmation(
        this.intl.t('new-settings.channels.email.advanced.trusted-senders.email-added'),
      );
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.channels.email.advanced.trusted-senders.not-saved'),
      );
      this.expandAddEmailForm();
    }
  }

  @dropTask
  *addNewDomain(domain: string): TaskGenerator<void> {
    if (!this.isValidDomain(domain)) {
      this.notificationsService.notifyWarning(
        this.intl.t('new-settings.channels.email.advanced.trusted-senders.enter-valid-domain'),
      );
      return;
    }

    let wildcardDomain = `*@${domain.toLowerCase()}`;
    if (this.duplicateEmailAddress(wildcardDomain)) {
      this.notificationsService.notifyWarning(
        this.intl.t('new-settings.channels.email.advanced.trusted-senders.domain-already-added'),
      );
      return;
    }

    this.collapseAddDomainForm();
    try {
      let response = yield post('/ember/trusted_email_addresses', {
        app_id: this.appService.app.id,
        email_address: domain, // Backend will convert to wildcard format
      });

      // Add to local list (no reload needed - smoother UX)
      this.trustedEmailAddresses = [...this.trustedEmailAddresses, response];

      this.notificationsService.notifyConfirmation(
        this.intl.t('new-settings.channels.email.advanced.trusted-senders.domain-added'),
      );
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.channels.email.advanced.trusted-senders.not-saved'),
      );
      this.expandAddDomainForm();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Channels::Email::Advanced::TrustedSenders': typeof TrustedSenders;
  }
}
